import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import type { ProjectStatus } from '../api/types'

const STATUS_STYLE: Record<ProjectStatus, { label: string; color: string; bg: string }> = {
  PLANNED: { label: 'Planned', color: '#334e68', bg: '#e3e9ef' },
  IN_PROGRESS: { label: 'In progress', color: '#0b5d3b', bg: '#dcf2e6' },
  ON_HOLD: { label: 'On hold', color: '#8a5200', bg: '#fdeecf' },
  COMPLETED: { label: 'Completed', color: '#14532d', bg: '#d6f5df' },
  CLOSED: { label: 'Closed', color: '#3f4b56', bg: '#e6e9ec' },
  CANCELLED: { label: 'Cancelled', color: '#8c1d18', bg: '#fbe0de' },
}

export function StatusChip({ status }: { status: ProjectStatus }) {
  const style = STATUS_STYLE[status] ?? { label: status, color: '#334e68', bg: '#e3e9ef' }
  return (
    <Chip
      label={style.label}
      size="small"
      sx={{
        color: style.color,
        backgroundColor: style.bg,
        fontWeight: 600,
        borderRadius: 1,
      }}
    />
  )
}

/**
 * Whose material or labour a line is: ours, a contractor's, or client-issued.
 *
 * <p>Deliberately louder than the supplier or contractor name beside it. Which side of the
 * account a line belongs to is what a reviewer scans for, and it is the one thing that
 * cannot be inferred from a name sitting in a column.</p>
 */
export function ScopeChip({ scope, party }: { scope: string; party?: string }) {
  const own = scope === 'OWN'
  const client = scope === 'CLIENT'
  const label = own ? 'Own' : client ? 'Client issued' : `Contractor${party ? `: ${party}` : ''}`
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: 1,
        ...(own
          ? { backgroundColor: '#dceaf8', color: '#0d4a8a' }
          : client
            ? { backgroundColor: '#ede7f6', color: '#4527a0' }
            : { backgroundColor: '#fdeecf', color: '#9a4a00' }),
      }}
    />
  )
}

/**
 * An actual figure read against the plan it was measured against.
 *
 * <p>Shortfall is coloured, surplus is not: a gang that came up short is what a manager
 * has to act on tonight, while more workers than planned is worth seeing but rarely worth
 * chasing. A zero against a plan reads as "not counted yet", which is what it usually is
 * on a line an applied template has just created.</p>
 */
export function ActualAgainstPlan({ actual, planned }: { actual: number; planned?: number }) {
  if (planned == null) {
    return <strong>{actual}</strong>
  }
  if (actual === 0) {
    return (
      <Chip
        size="small"
        label="not counted"
        sx={{ fontWeight: 600, borderRadius: 1, backgroundColor: '#fdeecf', color: '#9a4a00' }}
      />
    )
  }
  const short = actual < planned
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <strong style={{ color: short ? '#b3261e' : undefined }}>{actual}</strong>
      <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, ml: 0.5 }}>
        {short ? `(-${planned - actual})` : actual > planned ? `(+${actual - planned})` : '(on plan)'}
      </Box>
    </Box>
  )
}

export function ActiveChip({ active }: { active: boolean }) {
  return (
    <Chip
      label={active ? 'Active' : 'Inactive'}
      size="small"
      variant={active ? 'filled' : 'outlined'}
      sx={{
        fontWeight: 600,
        borderRadius: 1,
        ...(active
          ? { backgroundColor: '#dcf2e6', color: '#0b5d3b' }
          : { color: '#6b7783', borderColor: '#c6ced6' }),
      }}
    />
  )
}
